import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Switch, Button, Space, App, Alert, Tag, Typography, Divider } from 'antd';
import {
  SaveOutlined, ReloadOutlined, GoogleOutlined, LinkOutlined, CheckCircleTwoTone, WarningTwoTone,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi } from '@zero/auth-admin-ui';
import { invalidateAppConfigCache } from '@zero/auth-client';

const { Text, Paragraph } = Typography;

/**
 * GoogleOAuthPage — admin chỉnh runtime Auth.Google.* trong Auth_AppConfig.
 *
 * 4 key được quản:
 *   Auth.Google.Enabled              (Boolean, public=1) — bật nút trên login page
 *   Auth.Google.ClientID             (String,  public=1) — FE cần đọc để render GoogleLogin
 *   Auth.Google.AllowedEmailDomains  (String,  public=0) — CSV domain cho phép, vd "gmail.com,googlemail.com"
 *   Auth.Google.AutoLink             (Boolean, public=0) — lần đầu login Google có auto-lưu sub không
 *
 * Sau khi save → invalidate cache FE → LoginForm reload & render Google button.
 *
 * Ghi chú bảo mật: KHÔNG yêu cầu ClientSecret. Phương thức "Sign in with Google for Web"
 * dùng ID token verify bằng JWKS — BE chỉ cần ClientID làm audience.
 */
const GOOGLE_KEYS = [
  { key: 'Auth.Google.Enabled',             field: 'Enabled',             type: 'bool',   isPublic: true  },
  { key: 'Auth.Google.ClientID',            field: 'ClientID',            type: 'string', isPublic: true  },
  { key: 'Auth.Google.AllowedEmailDomains', field: 'AllowedEmailDomains', type: 'string', isPublic: false },
  { key: 'Auth.Google.AutoLink',            field: 'AutoLink',            type: 'bool',   isPublic: false },
];

// Format chuẩn của Google OAuth Client ID — dùng để gợi ý cho admin
const CLIENT_ID_SUFFIX = '.apps.googleusercontent.com';
const CLIENT_ID_PATTERN = /^[0-9]+-[a-z0-9]+\.apps\.googleusercontent\.com$/i;

export function GoogleOAuthPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [snapshot, setSnapshot] = useState(null);   // lưu giá trị server-side cuối cùng (cho hiển thị status)

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.listAppConfig();
      const list = r.data?.Data ?? [];
      const lookup = (key) => list.find((e) => e.ConfigKey === key)?.ConfigValue ?? '';
      const values = {};
      for (const k of GOOGLE_KEYS) {
        const raw = lookup(k.key);
        if (k.type === 'bool') values[k.field] = raw === 'true' || raw === '1';
        else values[k.field] = raw;
      }
      form.setFieldsValue(values);
      setSnapshot(values);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const save = async (values) => {
    setSaving(true);
    try {
      for (const k of GOOGLE_KEYS) {
        let raw = values[k.field];
        if (k.type === 'bool') raw = raw ? 'true' : 'false';
        if (typeof raw === 'string') raw = raw.trim();
        if (raw === '') raw = null;
        await api.upsertAppConfig(k.key, {
          ConfigKey:   k.key,
          ConfigValue: raw,
          ValueType:   k.type === 'bool' ? 'Boolean' : 'String',
          IsPublic:    k.isPublic,
        });
      }
      invalidateAppConfigCache();   // LoginForm + GoogleLoginButton sẽ refetch
      setSnapshot(values);
      message.success(t('common.savedAt', { defaultValue: 'Đã lưu lúc' }) + ' ' + new Date().toLocaleTimeString());
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  const validateClientID = (_, val) => {
    if (!val) return Promise.resolve();
    if (!CLIENT_ID_PATTERN.test(val))
      return Promise.reject(new Error(`Client ID không đúng định dạng — phải kết thúc bằng "${CLIENT_ID_SUFFIX}"`));
    return Promise.resolve();
  };

  // Status banner — đọc snapshot server-side, không phụ thuộc state form đang gõ
  const renderStatus = () => {
    if (!snapshot) return null;
    if (!snapshot.Enabled) {
      return <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Đăng nhập Google đang TẮT — nút sẽ không hiển thị trên trang đăng nhập."
      />;
    }
    if (!snapshot.ClientID) {
      return <Alert
        type="warning" showIcon style={{ marginBottom: 12 }}
        icon={<WarningTwoTone twoToneColor="#faad14" />}
        message="Đã bật Google login nhưng chưa cấu hình Client ID — nút Google sẽ không render."
      />;
    }
    if (!CLIENT_ID_PATTERN.test(snapshot.ClientID)) {
      return <Alert
        type="warning" showIcon style={{ marginBottom: 12 }}
        message={`Client ID không đúng định dạng (phải kết thúc bằng ${CLIENT_ID_SUFFIX}).`}
      />;
    }
    return <Alert
      type="success" showIcon style={{ marginBottom: 12 }}
      icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
      message="Đăng nhập Google đang BẬT và đã cấu hình hợp lệ."
    />;
  };

  return (
    <Card size="small" extra={
      <Space size={4}>
        <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>Reload</Button>
      </Space>
    }>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Cấu hình đăng nhập Google"
        description={
          <span>
            Cho phép người dùng đăng nhập bằng tài khoản Google (Gmail / Google Workspace). User <strong>phải</strong> có
            email khớp với một <Tag style={{ marginInline: 4 }}>Auth_User</Tag> đang active — hệ thống KHÔNG tự động
            tạo user mới. Cấu hình lưu trong DB (Auth_AppConfig), thay đổi áp dụng tức thì không cần restart server.
          </span>
        }
      />

      {renderStatus()}

      <Form form={form} layout="vertical" onFinish={save} disabled={loading} autoComplete="off">
        <Space align="start" size="large" wrap>
          <Form.Item
            label="Bật Google login"
            name="Enabled"
            valuePropName="checked"
            extra="Nút sẽ hiển thị trên trang /login khi đã cấu hình Client ID hợp lệ."
          >
            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>

          <Form.Item
            label="Tự liên kết tài khoản (AutoLink)"
            name="AutoLink"
            valuePropName="checked"
            extra="Lần đầu user đăng nhập Google: lưu Google `sub` vào Auth_User để cross-check lần sau."
          >
            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
          </Form.Item>
        </Space>

        <Form.Item
          label="Google OAuth Client ID"
          name="ClientID"
          rules={[{ validator: validateClientID }]}
          extra={
            <span>
              Tạo tại <Text code>console.cloud.google.com → APIs &amp; Services → Credentials → Create credentials → OAuth client ID → Web application</Text>.
              Thêm <Text code>Authorized JavaScript origins</Text> = domain frontend (vd <Text code>https://yourapp.vn</Text>).
            </span>
          }
        >
          <Input
            prefix={<GoogleOutlined />}
            placeholder={`123456789-abcdef0123${CLIENT_ID_SUFFIX}`}
            allowClear
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item
          label="Domain email được phép"
          name="AllowedEmailDomains"
          extra={
            <span>
              CSV (phân cách dấu phẩy). Mặc định <Text code>gmail.com,googlemail.com</Text> — chỉ cho phép tài khoản Gmail
              cá nhân. Đổi thành domain công ty (vd <Text code>vmtmateco.vn</Text>) khi dùng Google Workspace.
            </span>
          }
        >
          <Input placeholder="gmail.com,googlemail.com" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
            Lưu cấu hình
          </Button>
        </Form.Item>
      </Form>

      <Divider style={{ margin: '16px 0' }} />

      <Alert
        type="warning" showIcon
        message="Hướng dẫn nhanh"
        description={
          <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
            <li>Tạo <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">
              Google OAuth Client ID <LinkOutlined /></a> dạng Web application.</li>
            <li>Trong "Authorized JavaScript origins", thêm origin của frontend (vd <Text code>http://localhost:5173</Text> cho dev, <Text code>https://yourapp.vn</Text> cho prod).</li>
            <li>Copy Client ID dán vào ô trên (kết thúc <Text code>{CLIENT_ID_SUFFIX}</Text>).</li>
            <li>Đảm bảo user đã có record trong <Text code>Auth_User</Text> với <Text code>Email</Text> = Google email (không phân biệt hoa thường).</li>
            <li>Bật <Text strong>"Bật Google login"</Text> ở trên rồi lưu. Reload trang đăng nhập để thấy nút Google.</li>
          </ol>
        }
      />

      <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
        Bảo mật: phương thức này dùng Google ID token verify bằng JWKS public — không cần lưu ClientSecret trên server.
        BE cross-check Google <Text code>sub</Text> với <Text code>ExternalSubjectID</Text> đã link để chống account takeover
        nếu admin lỡ đổi email user thành email của attacker.
      </Paragraph>
    </Card>
  );
}
